import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, RefreshCw, ExternalLink, 
  FileText, Copy, Clock, CheckCircle
} from 'lucide-react';

const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// Available template variables by context
const TEMPLATE_VARIABLES = {
  common: [
    { name: 'player_name', description: 'Recipient name' },
    { name: 'player_email', description: 'Recipient email' },
    { name: 'league_name', description: 'League name' },
    { name: 'season_name', description: 'Season name' },
  ],
  invite: [
    { name: 'inviter_name', description: 'Who sent the invite' },
    { name: 'invite_link', description: 'Join link' },
  ],
  draft: [
    { name: 'draft_position', description: 'Current pick position' },
    { name: 'time_remaining', description: 'Time left to pick' },
  ],
  scoring: [
    { name: 'episode_number', description: 'Episode number' },
    { name: 'score', description: 'Points earned' },
    { name: 'rank', description: 'Current ranking' },
  ]
};

export default function CommsTemplates() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadTemplates();
  }, [user, navigate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const [triggersRes, dripsRes] = await Promise.all([
        axios.get(`${API}/platform/email-triggers`, getAuthConfig()),
        axios.get(`${API}/platform/drip-campaigns`, getAuthConfig()).catch(() => ({ data: [] }))
      ]);

      // Extract email templates from triggers
      const emailTpls = [];
      const smsTpls = [];
      
      (triggersRes.data || []).forEach(t => {
        if (t.template_id) {
          emailTpls.push({
            id: t.template_id,
            name: t.event_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            usedBy: [t.event_id],
            type: 'trigger'
          });
        }
        if (t.sms_message) {
          smsTpls.push({
            id: t.event_id,
            name: t.event_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            message: t.sms_message,
            usedBy: [t.event_id],
            type: 'trigger'
          });
        }
      });

      (dripsRes.data || []).forEach(d => {
        if (d.template_id_email_1) {
          emailTpls.push({
            id: d.template_id_email_1,
            name: `${d.name} - Step 1`,
            usedBy: [d.id],
            type: 'drip'
          });
        }
        if (d.template_id_email_2) {
          emailTpls.push({
            id: d.template_id_email_2,
            name: `${d.name} - Step 2`,
            usedBy: [d.id],
            type: 'drip'
          });
        }
        if (d.sms_1_message) {
          smsTpls.push({
            id: `${d.id}_sms_1`,
            name: `${d.name} - Step 1`,
            message: d.sms_1_message,
            usedBy: [d.id],
            type: 'drip'
          });
        }
        if (d.sms_2_message) {
          smsTpls.push({
            id: `${d.id}_sms_2`,
            name: `${d.name} - Step 2`,
            message: d.sms_2_message,
            usedBy: [d.id],
            type: 'drip'
          });
        }
      });

      setEmailTemplates(emailTpls);
      setSmsTemplates(smsTpls);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin/comms">
      <div className="space-y-6" data-testid="comms-templates">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Templates</h1>
            <p className="text-sm text-zinc-500">Content library for notifications</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadTemplates}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Tabs defaultValue="email" className="space-y-4">
          <TabsList className="bg-zinc-800 border border-zinc-700">
            <TabsTrigger value="email" className="data-[state=active]:bg-blue-600">
              <Mail className="w-4 h-4 mr-2" />
              Email Templates
            </TabsTrigger>
            <TabsTrigger value="sms" className="data-[state=active]:bg-emerald-600">
              <MessageSquare className="w-4 h-4 mr-2" />
              SMS Templates
            </TabsTrigger>
            <TabsTrigger value="variables" className="data-[state=active]:bg-amber-600">
              <FileText className="w-4 h-4 mr-2" />
              Variables
            </TabsTrigger>
          </TabsList>

          {/* Email Templates */}
          <TabsContent value="email" className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : emailTemplates.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Mail className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
                  <p className="text-zinc-400">No email templates configured yet</p>
                  <p className="text-sm text-zinc-500 mt-1">
                    Add SendGrid template IDs in the Notifications settings
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {emailTemplates.map((template, idx) => (
                  <Card key={idx} className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                            <Mail className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <h3 className="font-medium text-zinc-100">{template.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="text-xs text-blue-400 bg-zinc-800 px-2 py-0.5 rounded font-mono">
                                {template.id}
                              </code>
                              <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">
                                {template.type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(template.id)}
                            className="text-zinc-500 hover:text-zinc-100"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <a
                            href={`https://mc.sendgrid.com/dynamic-templates/${template.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm" className="text-zinc-500 hover:text-zinc-100">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <ExternalLink className="w-5 h-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-zinc-400">
                      Email templates are managed in SendGrid. Create dynamic templates there, then add the template ID to your notifications.
                    </p>
                    <a
                      href="https://mc.sendgrid.com/dynamic-templates"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm mt-2"
                    >
                      Open SendGrid Dynamic Templates
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Templates */}
          <TabsContent value="sms" className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : smsTemplates.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
                  <p className="text-zinc-400">No SMS templates configured yet</p>
                  <p className="text-sm text-zinc-500 mt-1">
                    Add SMS messages in the Notifications settings
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {smsTemplates.map((template, idx) => (
                  <Card key={idx} className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-zinc-100">{template.name}</h3>
                              <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">
                                {template.type}
                              </Badge>
                            </div>
                            <p className="text-sm text-zinc-400 mt-2 bg-zinc-800 p-2 rounded font-mono break-words">
                              {template.message}
                            </p>
                            <p className="text-xs text-zinc-500 mt-2">
                              {template.message.length} characters
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(template.message)}
                          className="text-zinc-500 hover:text-zinc-100 flex-shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Variables Reference */}
          <TabsContent value="variables" className="space-y-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base text-zinc-100">Common Variables</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {TEMPLATE_VARIABLES.common.map(v => (
                    <div key={v.name} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <div>
                        <code className="text-emerald-400 text-sm font-mono">{`{{${v.name}}}`}</code>
                        <p className="text-xs text-zinc-500 mt-0.5">{v.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(`{{${v.name}}}`)}
                        className="text-zinc-500 hover:text-zinc-100"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base text-zinc-100">Invite Variables</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {TEMPLATE_VARIABLES.invite.map(v => (
                    <div key={v.name} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <div>
                        <code className="text-emerald-400 text-sm font-mono">{`{{${v.name}}}`}</code>
                        <p className="text-xs text-zinc-500 mt-0.5">{v.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(`{{${v.name}}}`)}
                        className="text-zinc-500 hover:text-zinc-100"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base text-zinc-100">Draft Variables</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {TEMPLATE_VARIABLES.draft.map(v => (
                    <div key={v.name} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <div>
                        <code className="text-emerald-400 text-sm font-mono">{`{{${v.name}}}`}</code>
                        <p className="text-xs text-zinc-500 mt-0.5">{v.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(`{{${v.name}}}`)}
                        className="text-zinc-500 hover:text-zinc-100"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base text-zinc-100">Scoring Variables</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {TEMPLATE_VARIABLES.scoring.map(v => (
                    <div key={v.name} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <div>
                        <code className="text-emerald-400 text-sm font-mono">{`{{${v.name}}}`}</code>
                        <p className="text-xs text-zinc-500 mt-0.5">{v.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(`{{${v.name}}}`)}
                        className="text-zinc-500 hover:text-zinc-100"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </SuperAdminLayout>
  );
}
